import { CurdDataSetConfig, FormFieldType, Mobile } from '../types';
import { durationToString, toDateFormat, toDateTimeFormat } from '@mri/functions';
import { ChoiceTypes, PermissionPrefixKey } from '@mri/enums';
import { propertyCommonField } from './property-common-field';
import { MobileSensorsConfigComponent } from '@mri/app/modules';

function badgePositionTimeClass ( element: Mobile )  {
  const lastPositionTime = element.interaction?.lastPositionTime
  if (!lastPositionTime) return '';

  const startTime = new Date(lastPositionTime);
  const endTime = new Date();
  const duration = Math.abs(endTime.getTime() - startTime.getTime());

  if (duration > 1000 * 60 * 5) return 'badge text-warn-900 bg-warn-50';

  return 'badge text-primary-900 bg-primary-50';
}

function badgePositionTime(element: Mobile){
  const lastPositionTime = element.interaction?.lastPositionTime

  if(!lastPositionTime) return '';

  const classValue = badgePositionTimeClass(element);

  const startTime = new Date(lastPositionTime);
  const endTime = new Date();
  return `<span class="${classValue}">${durationToString(Math.abs(endTime.getTime() - startTime.getTime()))}</span>`;
}

const filterConfig = [
  { key: 'name', label: 'Placa', type: FormFieldType.STRING },
  {
    key: 'deviceId',
    label: 'Dispositivo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'devices'
  },
  {
    key: 'propertyId',
    label: 'Propietario',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsGateway: 'properties'
  },
  {
    key: 'type',
    label: 'Tipo del Vehiculo',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsConfigKey: ChoiceTypes.MOBILE_TYPE
  },
  {
    key: 'icon',
    label: 'Icono',
    type: FormFieldType.OPTIONS_MULTIPLE,
    optionsConfigKey: ChoiceTypes.MOBILE_ICON
  }
];

export const mobileDataSetConfig: CurdDataSetConfig<Mobile> = {
  entityName: 'Vehiculo',
  collectionName: 'Vehicles',
  gatewayUrl: 'mobiles',
  baseBreadcrumb: 'mobiles',
  permissionPrefix: PermissionPrefixKey.MOBILE,
  form: {
    listRedirectUrl: 'console/mobiles/list',
    fields: [
      {
        key: 'name',
        label: 'Placa',
        type: FormFieldType.STRING,
        containerClass: 'col-md-6 col-sm-6'
      },
      {
        key: 'type',
        label: 'Tipo del Vehiculo',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.MOBILE_TYPE
      },
      {
        key: 'icon',
        label: 'Icono',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.MOBILE_ICON,
        fillDefaultValue: () => 'default'
      },
      {
        key: 'model',
        label: 'Modelo',
        type: FormFieldType.NUMBER,
        min: new Date().getFullYear() - 100,
        max: new Date().getFullYear(),
        containerClass: 'col-md-6 col-sm-6'
      },
      {
        key: 'deviceId',
        label: 'Dispositivo',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-12 col-sm-12',
        optionsGateway: 'devices'
      },

      {
        key: 'distanceUnit',
        label: 'Unidad de distancia',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.DISTANCE
      },
      {
        key: 'speedUnit',
        label: 'Unidad de velocidad',
        type: FormFieldType.OPTIONS,
        containerClass: 'col-md-6 col-sm-6',
        optionsConfigKey: ChoiceTypes.SPEED
      },

      {
        key: 'permitCommand',
        type: FormFieldType.BOOLEAN,
        label: 'Permitir comandos',
        containerClass: 'col-md-6 col-sm-6'
      },
      propertyCommonField
    ]
  },
  list: {
    pageConfig: {
      title: 'Lista de vehiculos',
      breadcrumb: [
        {
          title: 'Vehiculos',
          type: 'item',
          link: '/console/mobiles/list'
        },
        {
          title: 'Lista',
          type: 'item',
          link: '/console/mobiles/list',
          pageName: 'Vehiculos'
        }
      ]
    },
    table: {
      columns: [
        {
          key: 'name', label: 'Placa',
          sort: {
            id: 'name',
            fieldKey: 'name'
          }
        },
        {
          key: 'uniqueId', label: 'Dispositivo',
          getValue: ({ element }) => element.device?.name || 'No definido'
        },
        {
          key: 'phoneLine', label: 'Telefono',
          getValue: ({ element }) => element.simCard?.name || 'No definido'
        },
        {
          key: 'lastPositionTime', label: 'Ultima posición',
          getValue: ({ element }) => {
            if(!element.interaction?.lastPositionTime ) return 'Nunca';

            const body = toDateTimeFormat(element.interaction?.lastPositionTime);
            const badge = badgePositionTime(element);

            return `${body} <span class="hidden">|</span> ${badge}`;
          },
          sort: {
            id: 'lastPositionTime',
            fieldKey: 'lastPositionTime'
          }
        },
        {
          key: 'owner', label: 'Dueño',
          getValue: ({ element }) => element.property?.name || 'No definido'
        },
        {
          key: 'createdTime', label: 'Fecha de creación',
          getValue: ({ element }) => toDateFormat(element.createdTime)
        }
      ]
    },
    actionsKeys: ['create', 'detail','edit', 'transfer', 'delete'],
    filter: filterConfig,
    searchFilter: filterConfig,
    extraActions: [

      {
        key: 'alarms-config',
        label: 'Alarmas',
        icon: 'fi-rr-bell',
        //onlyIcon: () => true,
        disableWhen: p => p.selectedElements?.length !== 1,
        link: ({ selectedElements }) => {
          const [element] = selectedElements || [];
          if(!element) return [];

          return ['/' ,'console', 'mobiles', element._id, 'alarms']
        }
      },
      {
        key: 'sensors-config',
        label: 'Sensores',
        icon: 'fi-rr-bolt',
        onlyIcon: () => true,
        disableWhen: p => p.selectedElements?.length !== 1,
        click: ({ selectedElements, component }) => {
          const [element] = selectedElements || [];

          if (!element) return;

          component?.openDrawer(
            `Configurar sensores de ${element.name}`,
            MobileSensorsConfigComponent,
            { mobile: element }
          );
        }
      },
    ]
  },
  pages: ['list', 'create', 'edit']
};
